import { useState, useEffect, useRef } from "react";
import { BusquedaAPI } from "../../services/api/Busqueda";
import _ from "lodash";
import { useQueryParams } from "../../hooks/useQueryParams";
import { useHistory, useLocation } from "react-router-dom";
import SEARCH_FILTERS from "../filtros-busqueda";
import { useImmer } from "use-immer";


/**
 * Hook personalizado para gestionar el estado de filtros y la búsqueda de resultados.
 * 
 * Este hook maneja la lógica de:
 * - Cargar filtros desde los parámetros de la URL.
 * - Activar o desactivar filtros.
 * - Detectar si hay cambios en los filtros.
 * - Ejecutar búsquedas según los filtros activos.
 * - Construir la URL correspondiente a los filtros.
 * - Resetear todos los filtros.
 * - Sincronizar filtros con la API.
 * 
 * Utiliza internamente `useImmer` para manipular filtros de manera inmutable,
 * y hace peticiones a la API para obtener resultados con los filtros aplicados.
 * 
 * @returns {Object} Objeto con funciones y estados clave para la gestión de filtros y búsqueda:
 * 
 * @property {boolean} loading - Indica si actualmente se está cargando la información de resultados.
 * 
 * @property {Array} data - Arreglo de resultados obtenidos de la búsqueda.
 * 
 * @property {Function} search - Función para realizar una búsqueda. Puede recibir un objeto con las siguientes propiedades:
 *   @param {Object} props
 *   @param {Object} [props.apiFilters=null] - Filtros ya preparados en formato de la API.
 *   @param {boolean} [props.customFilters=false] - Si `true`, se usará `apiFilters` en lugar del formato interno.
 *   @param {boolean} [props.force=false] - Si `true`, forzará una nueva búsqueda aunque los filtros no hayan cambiado.
 * 
 * @property {boolean} hasChanges - Indica si hay cambios pendientes en los filtros respecto a la última búsqueda realizada.
 * 
 * @property {Object} filters - Objeto que contiene todos los filtros disponibles (temas, edad, género, etc.).
 * 
 * @property {Function} updateFilters - Función para actualizar el estado de los filtros (equivale a `setFilters`).
 * 
 * @property {Function} deleteAllFilters - Restablece todos los filtros a su estado inicial y actualiza la URL.
 * 
 * @property {boolean} hasFilters - Indica si hay al menos un filtro activo.
 * 
 * @property {Function} updateUrlParams - Actualiza los parámetros de la URL con los filtros actuales.
 *   @param {Object} props
 *   @param {boolean} [props.resetPage=true] - Si `true`, resetea el número de página a 1.
 *   @param {Object} [props.apiFilters=null] - Filtros en formato de la API (usado si `customFilters` es `true`).
 *   @param {boolean} [props.customFilters=false] - Si `true`, se usará `apiFilters` en lugar del formato interno.
 * 
 * @property {Function} getActiveFilters - Función que extrae y devuelve un arreglo con todos los filtros actualmente activos.
 *   @param {Object} filters - Objeto completo de filtros.
 *   @returns {Array} Lista de filtros activos.
 * 
 * @property {Function} parseFiltersToAPIFormat - Convierte los filtros activos al formato esperado por la API.
 *   @param {Array} filters - Lista de filtros activos.
 *   @returns {Object} Objeto con los filtros listos para ser enviados en una petición.
 * 
 * @property {Function} forceSearch - Fuerza una búsqueda con los filtros actuales, actualizando la URL y solicitando resultados.
 */

export const useFilterAndSearchManager = () => {
	const [loading, setLoading] = useState(true);
	const [data, setData] = useState([]);
	const [hasChanges, setHasChanges] = useState(false);
	const params = useQueryParams();
	const location = useLocation();
	const shouldForceSearchByParams = location?.state?.forceSearchByParams || false;
	const history = useHistory();
	const [filters, setFilters] = useImmer(_.cloneDeep(SEARCH_FILTERS));
	const [activeFilters, setActiveFilters] = useState([]);
	const [prevFilters, setPrevFilters] = useState([]);
	const [apiFormatFilters, setApiFormatFilters] = useState({});
	const hasFilters = activeFilters && activeFilters.length > 0;
	const filteringRef = useRef(false);
	const hasInitialized = useRef(false);
	const [shouldFetchBooksFromParams, setShouldFetchBooksFromParams] = useState(false);
	const shouldSearchRef = useRef(false);

	const loadFiltersByParams = async () => {
		for (const [key, v] of params.entries()) {	
			const value = encodeURIComponent(v);	// ya viene decodificado, volvemos a encodificar para evitar problemas en otras partes de esta función que intenta decodificar	
			if (["tema", "edad", "genero", "nivel", "dificultadLectora", "tag"].includes(key)) {
				setFilters((draft) => {
					if (key === "dificultadLectora") {
						const split = decodeURIComponent(value);
						draft[key].items = draft[key]?.items?.map((item) => {
							return {
								...item,
								active: split.includes(item?.paramValue)
									? true
									: false,
							};
						});
					} else {
						const split = decodeURIComponent(value).split(";");
						if (key === "tag") {
							if (!draft[key].items) {
								draft[key].items = [];
								draft[key].active = true;
								split.forEach((val) => {
									draft[key].items.push({
										paramValue: val,
										active: true,
										displayLabel: {
											default: val,
											translationId: val,
										},
									});
								});
							}
						}
						draft[key].items = draft[key]?.items?.map((item) => {
							return {
								...item,
								active: split.find((val) => val === item?.paramValue)
									? true
									: false,
							};
						});
					}
				});
			} else {
				setFilters((draft) => {
					const element = filters[key];
					if (element) {
						draft[key].paramValue = decodeURIComponent(value);
						draft[key].active = true;
						console.log("filter", key, decodeURIComponent(value), draft[key]);
					}
				});
			}
		}
		setShouldFetchBooksFromParams(true);
	};

	const parseFiltersToAPIFormat = (_filters) => {
		let result = {};
		if (Array.isArray(_filters)) {
			_filters.forEach((fil) => {
				if (fil?.displayType === "multicheckbox") {
					if (fil?.paramValue === "dificultadLectora") {
						result[fil?.paramKey] = fil?.items?.map((el) => el?.paramValue).join(";");
					} else {
						result[fil?.paramKey] = fil?.items?.map((el) => el?.paramValue).join(";");
					}
				}
				if (fil?.displayType === "checkbox") {
					result[fil?.paramKey] = 1;
				}
				if (fil?.displayType === "input-text") {
					result[fil?.paramKey] = fil?.paramValue;
				}
				if (fil?.displayType === "sort-dropdown") {
					result[fil?.paramKey] = fil?.paramValue;
				}
				if (fil?.displayType === null) {
					if (fil?.paramKey === "tag") {
						result[fil?.paramKey] = fil?.items?.map((el) => el?.paramValue).join(";");
					} else {
						result[fil?.paramKey] = fil?.paramValue;
					}
				}
			});
		}
		return result;
	};

	const getActiveFilters = (_filters) => {
		return Object.entries(_filters).reduce((acc, curr) => {
			if (curr[1].displayType === "multicheckbox") {
				const items = curr[1].items.filter((item) => item.active === true);
				if (items.length > 0) acc = [...acc, { ...curr[1], items: items }];
			} else if (curr[1].displayType === "checkbox") {
				if (curr[1].active) acc.push(curr[1]);
			} else if (curr[1].displayType === "input-text") {
				if (Boolean(curr[1].paramValue))
					acc.push({
						...curr[1],
						displayLabel: {
							default: curr[1].paramValue,
							translationId: curr[1].paramValue,
						},
					});
			} else if (curr[1].displayType === "sort-dropdown") {
				if (curr[1].active)
					acc.push({
						...curr[1],
						displayLabel: {
							default: curr[1].paramValue,
							translationId: curr[1].paramValue,
						},
					});
			} else if (curr[1].displayType === null) {
				if (curr[1].paramKey === "tag" && Array.isArray(curr[1].items)) {
					const items = curr[1].items.filter((item) => item.active === true);
					if (items.length > 0) acc = [...acc, { ...curr[1], items: items }];
				} 
				else {
					if (curr[1].active)
						acc.push({
							...curr[1],
							displayLabel: {
								default: curr[1].paramValue,
								translationId: curr[1].paramValue,
							},
						});
				}
			}
			return acc;
		}, []);
	};

	const getSearchParams = ({ filters }) => {
		const params = Object.entries(filters)
			.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
			.join("&");
		return params;
	};

	const search = async (props) => {
		const { apiFilters=null, customFilters = false, force = false } = props || {};
		if (!_.isEqual(prevFilters, filters) || force) {
			filteringRef.current = true;
			try {
				setPrevFilters(activeFilters);
				setLoading(true);
				await fetchBooks({ filters: customFilters ? apiFilters : apiFormatFilters });
			} catch (err) {
				console.error(err);
			} finally {
				setLoading(false);
			}
		}
	};

	const fetchBooks = async ({ filters }) => {
		await BusquedaAPI.obtenerLibros({
			abortSignal: null,
			data: filters,
		})
			.then((res) => {
				if (params.has("maxBook")) {
					const number = params.get("maxBook")
					setData(Array.isArray(res) ? res.slice(0, number) : [])
				} else {
					setData(Array.isArray(res) ? res : [])
				}
			})
			.catch((err) => console.error(err));
	};

	const deleteAllFilters = async () => {
		setLoading(true);
		setFilters(_.cloneDeep(SEARCH_FILTERS));
		setHasChanges(false);
		setActiveFilters([]);
		setPrevFilters([]);
		updateUrlParams({ resetPage: true });
		const newParams = new URLSearchParams();
		history.replace({ pathname: "/busqueda", search: newParams.toString() });
		if (!filteringRef.current !== true) {
			await fetchBooks({ filters: {} });
		}
		filteringRef.current = false;
		setLoading(false);
	};

	const updateUrlParams = (props) => {
		const { resetPage = true, apiFilters=null, customFilters = false } = props;
		const newParams = new URLSearchParams(getSearchParams({ filters: customFilters? apiFilters : apiFormatFilters }));
		newParams.set("page", resetPage ? 1 : params.get("page") || 1);
		history.replace({ pathname: "/busqueda", search: newParams.toString() });
	};

	const forceSearch = () => {
		shouldSearchRef.current = true;
	};

	useEffect(() => {
		setHasChanges(!_.isEqual(activeFilters, prevFilters));
	}, [activeFilters, prevFilters]);

	const updateFilters = setFilters;

	useEffect(() => {
		setActiveFilters(getActiveFilters(filters));
	}, [filters]);

	useEffect(() => {
		setApiFormatFilters(parseFiltersToAPIFormat(activeFilters));
	}, [activeFilters]);

	useEffect(() => {
		if (!hasInitialized.current) {
			loadFiltersByParams();
			hasInitialized.current = true;
		}
	}, [params, hasInitialized]);

	useEffect(() => {
		const loadInitialData = async () => {
			if (shouldFetchBooksFromParams) {
				filteringRef.current = true;
				setLoading(true);
				setShouldFetchBooksFromParams(false);
				const activeFilter = getActiveFilters(filters);
				setPrevFilters(activeFilter);
				const apiFormatFilters = parseFiltersToAPIFormat(activeFilter);
				await fetchBooks({ filters: apiFormatFilters });
				setLoading(false);
			}
		};
		loadInitialData();
	}, [filters, shouldFetchBooksFromParams]);

	
	useEffect(()=>{
		const forceSearch = async () => {		
			if (shouldForceSearchByParams) {
				setFilters(_.cloneDeep(SEARCH_FILTERS));
				setActiveFilters([]);
				setPrevFilters([]);
				if(location.pathname === "/busqueda"){
					history.replace({ pathname: "/busqueda", search: `?${params.toString()}`, state:undefined });
				}
				loadFiltersByParams();
			}
		}
		forceSearch();
	},[shouldForceSearchByParams, params, location.pathname]);



	useEffect(() => {
		if (shouldSearchRef.current) {
			shouldSearchRef.current = false;
			const active = getActiveFilters(filters);
			const apiFilters = parseFiltersToAPIFormat(active);
			updateUrlParams({ resetPage: true, customFilters: true, apiFilters });
			search({ force: true, customFilters: true, apiFilters });
		}
	}, [filters]);

	return {
		loading,
		data,
		search,
		hasChanges,
		filters,
		updateFilters,
		deleteAllFilters,
		hasFilters,
		updateUrlParams,
		getActiveFilters,
		parseFiltersToAPIFormat,
		forceSearch,
	};
};
